import { useCallback, useState } from 'react'

import { useAppUserContext } from 'app/useAppUserContext'
import { useActionsContext } from 'features/actions/actionHooks'
import { cloneAction } from 'features/actions/utils/cloneAction'
import { getNewAction } from 'features/actions/utils/getNewAction'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { useToolbarContext } from './useToolbarContext'

export function useToolbarActionsActionsState() {
    const { objectSid } = useToolbarContext()
    const { actions = [], status } = useActionsContext()

    const { isAdmin } = useAppUserContext()
    const allowAddNewAction = isAdmin
    const allowEditAction = isAdmin
    const allowDeleteAction = isAdmin
    const allowDuplicateAction = isAdmin
    const allowModifyAction = allowEditAction || allowDuplicateAction || allowDeleteAction

    const isError = status === 'error'
    const isEmpty = actions.length === 0 && !isError
    const isLoading = status === 'loading' && !isError

    const [editingAction, setEditingAction] = useState<ActionDto | null>(null)

    const onCommitActionEdit = useCallback(() => {
        setEditingAction(null)
    }, [])

    const onAddNewAction = useCallback(() => {
        requestAnimationFrame(() => {
            const defaultAction = getNewAction(objectSid)
            setEditingAction(defaultAction)
        })
    }, [objectSid])

    const onEditAction = useCallback((action: ActionDto) => {
        requestAnimationFrame(() => {
            setEditingAction(action)
        })
    }, [])

    const onDuplicateAction = useCallback((action: ActionDto) => {
        requestAnimationFrame(() => {
            const newAction = cloneAction(action) as unknown as ActionDto
            setEditingAction(newAction)
        })
    }, [])

    const [actionToDelete, setActionToDelete] = useState<ActionDto | null>(null)
    const onDeleteActionModalOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setActionToDelete(null)
        }
    }, [])

    const onDeleteAction = useCallback((action: ActionDto) => {
        requestAnimationFrame(() => {
            setActionToDelete(action)
        })
    }, [])

    const [modifyActionMenuSid, setModifyActionMenuSid] = useState<string>('')
    const onModifyActionMenuOpenChange = useCallback((sid: string, open: boolean) => {
        // Prevent default open state from being overridden by the open prop.
        if (!open) {
            setModifyActionMenuSid('')
        }
    }, [])
    const onModifyActionClick = useCallback((sid: string) => {
        setModifyActionMenuSid(sid)
    }, [])

    return useDeepEqualsMemoValue({
        isEmpty,
        isLoading,
        isError,
        actions,
        allowAddNewAction,
        allowEditAction,
        allowDeleteAction,
        allowDuplicateAction,
        allowModifyAction,
        editingAction,
        onCommitActionEdit,
        onAddNewAction,
        onEditAction,
        onDuplicateAction,
        onDeleteAction,
        modifyActionMenuSid,
        onModifyActionMenuOpenChange,
        onModifyActionClick,
        actionToDelete,
        onDeleteActionModalOpenChange,
    })
}
